<template>
  <div class="main-container">
    <loading :active.sync="isLoading" :is-full-page="true" color="#4e97b2"></loading>
    <div v-if="this.city" class="container text-left">
      <BreadcrumbsHelper :breadcrumbs="this.breadcrumbs" />
      <div class="detail-header">
        <div>
          <h1>{{ city.name }}</h1>
          <p>
            Created: {{ new Date(city.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) }} | Last Updated:
            {{ new Date(city.updated_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) }}
            | Version: {{ city.version || 1 }}
          </p>
        </div>
        <template v-if="isCityEditAlreadyPending">
          <div v-if="this.$store.state.auth.mUser.role === 'admin'" class="city-admin-actions">
            <span>
              <img src="/images/notification-bell.svg" />
              <p>Pending Change Request</p>
            </span>
            <div>
              <b-button class="btn btn-white" @click="rejectCityChangeRequest(pendingCityRequest._id)">Reject</b-button>
              <b-button class="btn" @click="approveCityChangeRequest(pendingCityRequest._id)">Approve</b-button>
              <router-link v-if="city" :to="{ name: 'city edit', params: { cityId: city._id } }">
                <b-button class="btn"> {{ 'Edit' }} </b-button>
              </router-link>
            </div>
          </div>
          <div v-else>
            <b-button class="btn btn-primary disabled"> Suggestion Pending </b-button>
          </div>
        </template>
        <template v-else>
          <router-link v-if="city" :to="{ name: 'city edit', params: { cityId: city._id } }">
            <b-button class="btn btn-primary"> {{ this.$store.state.auth.mUser.role === 'admin' ? 'Edit' : 'Suggest an Edit' }} </b-button>
          </router-link>
        </template>
      </div>
      <div class="detail-section">
        <b-tabs>
          <b-tab title="Details" active>
            <h2>General Info</h2>
            <p><b>Name:</b> {{ city.name }}</p>
            <p><b>ID:</b> {{ city.id }}</p>
            <p v-if="city.areas[0]"><b>Area:</b> {{ city.areas[0].name }}</p>
            <p><b>City Type:</b> {{ getCityTypeDisplay(city.city_type) }}</p>
            <p><b>Description:</b> {{ city.description }}</p>
            <br />
            <p><b>Slug:</b> {{ city.slug }}</p>
            <p><b>Latitude:</b> {{ city.latitude }}</p>
            <p><b>Longitude:</b> {{ city.longitude }}</p>

            <template v-if="city.city_type === 0">
              <h2>Additional Content</h2>
              <p v-if="this.$store.state.auth.mUser.role === 'admin'"><b>Crowdriff ID:</b> {{ city.crowdriff_id }}</p>
              <p><b>Insider's Headline:</b> {{ city.insiders_headline }}</p>
              <p><b>Insider's Quote:</b> {{ city.insiders_quote }}</p>
              <p><b>Insider's Text:</b> {{ city.insiders_text }}</p>
              <p><b>Insider's Guide Url:</b> {{ city.insiders_url }}</p>
              <br />
              <p><b>Insider's Image:</b></p>
              <p><img :src="`${baseImageUrl}/uploads/city/${city.id}/${city.insiders_image}`" /></p>
              <p><b>Insider's Image Description:</b> {{ city.insiders_image_description }}</p>
              <br />
              <p><b>Directions by Air:</b> {{ city.directions_airplane }}</p>
              <br />
              <p><b>Directions by Bus:</b> {{ city.directions_bus }}</p>
              <br />
              <p><b>Directions by Car:</b> {{ city.directions_car }}</p>
            </template>

            <h2>SEO Content</h2>
            <p><b>SEO Title:</b> {{ city.seo_title }}</p>
            <p><b>SEO Meta Description:</b> {{ city.seo_meta_description }}</p>
            <p style="line-break: anywhere"><b>SEO Schema:</b> {{ city.seo_schema }}</p>

            <h2>Images</h2>
            <CityImageDisplay v-bind:photos="city.photos" :cityId="city.id" :canEdit="false" />
          </b-tab>
          <b-tab title="History">
            <br />
            <p>
              Current Version is {{ city.version || 1 }} <span v-if="city.updated_by">by {{ city.updated_by.first_name }} {{ city.updated_by.last_name }}</span>
            </p>
            <table v-if="cityVersions" class="table table-bordered history-table">
              <template v-for="cityVersion in cityVersions">
                <thead>
                  <tr>
                    <th colspan="3" class="text-center">
                      Version: {{ cityVersion.version }}
                      <span v-if="cityVersion.updated_at">
                        changed on
                        {{ new Date(cityVersion.updated_at).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) }} at
                        {{ new Date(cityVersion.updated_at).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }).toLowerCase() }}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Field</th>
                    <th>
                      Old <span v-if="cityVersion.oldUpdatedBy">({{ cityVersion.oldUpdatedBy.first_name }} {{ cityVersion.oldUpdatedBy.last_name }})</span>
                    </th>
                    <th>
                      New <span v-if="cityVersion.newUpdatedBy">({{ cityVersion.newUpdatedBy.first_name }} {{ cityVersion.newUpdatedBy.last_name }})</span>
                    </th>
                  </tr>
                  <tr v-for="change in cityVersion.changes" :key="change.key">
                    <td>{{ change.key }}</td>
                    <td>
                      {{ change.old }}
                    </td>
                    <td>
                      {{ change.new }}
                    </td>
                  </tr>
                  <tr v-for="imgChange in cityVersion.imageChanges" :key="imgChange.key">
                    <td>{{ imgChange.key }}</td>
                    <td style="width: 35%">
                      <b-card
                        v-if="imgChange.old"
                        :key="imgChange.old.image"
                        :img-src="`${baseImageUrl}/uploads/city/${city.id}/${imgChange.old.image}`"
                        img-alt=""
                        :no-body="imgChange.old.featured === undefined && !imgChange.old.description"
                        img-top
                        tag="article"
                        class="pending-img"
                        style="margin: auto"
                      >
                        <b-card-text>
                          <div v-if="imgChange.old.featured !== undefined"><b>Featured:</b> {{ imgChange.old.featured }}</div>
                          <div v-if="imgChange.old.description"><b>Description:</b> {{ imgChange.old.description }}</div>
                        </b-card-text>
                      </b-card>
                    </td>
                    <td style="width: 35%">
                      <b-card
                        v-if="imgChange.new"
                        :key="imgChange.new.image"
                        :img-src="`${baseImageUrl}/uploads/city/${city.id}/${imgChange.new.image}`"
                        img-alt=""
                        img-top
                        :no-body="imgChange.new.featured === undefined && !imgChange.new.description"
                        tag="article"
                        class="pending-img"
                        style="margin: auto"
                      >
                        <b-card-text>
                          <div v-if="imgChange.new.featured !== undefined"><b>Featured:</b> {{ imgChange.new.featured }}</div>
                          <div v-if="imgChange.new.description"><b>Description:</b> {{ imgChange.new.description }}</div>
                        </b-card-text>
                      </b-card>
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="!cityVersion.changes.length && !cityVersion.imageChanges.length">
                  <tr>
                    <td class="text-center" colspan="3">No changes made</td>
                  </tr>
                </tbody>
              </template>
            </table>
          </b-tab>
          <b-tab v-if="pendingCityRequest" title="Pending">
            <table class="table table-bordered history-table">
              <tbody>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
                <tr v-for="change in pendingCityChanges.fieldChanges" :key="change.field">
                  <td>{{ change.field }}</td>
                  <td>
                    {{ change.val }}
                  </td>
                </tr>
                <tr v-for="change in pendingCityChanges.imageChanges" :key="change.field">
                  <td>{{ change.field }}</td>
                  <td>
                    <div v-for="img in change.val" style="display: inline-flex">
                      <b-card
                        :key="img.image"
                        :no-body="img.featured === undefined && !img.description"
                        :img-src="`${baseImageUrl}/uploads/city/${city.id}/${img.image}`"
                        img-alt=""
                        img-top
                        tag="article"
                        class="pending-img"
                      >
                        <b-card-text>
                          <div v-if="img.featured !== undefined"><b>Featured:</b> {{ img.featured }}</div>
                          <div v-if="img.description !== undefined"><b>Description:</b> {{ img.description }}</div>
                        </b-card-text>
                      </b-card>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import axios from 'axios';
import deepEqual from 'deep-equal';
import BreadcrumbsHelper from '../common/BreadcrumbsHelper.vue';
import CityImageDisplay from './components/CityImageDisplay.vue';

export default {
  components: {
    Loading,
    BreadcrumbsHelper,
    CityImageDisplay,
  },
  props: {
    cityId: String,
  },
  async created() {
    this.getCity();
  },
  data() {
    return {
      isLoading: false,
      city: null,
      breadcrumbs: [],
      isCityEditAlreadyPending: false,
      pendingCityRequest: null,
      baseImageUrl: process.env.VUE_APP_S3_BASE_URL,
      cityVersions: [],
      pendingCityChanges: null,
    };
  },
  methods: {
    setBreadcrumbs() {
      this.breadcrumbs = [];

      if (this.$store.state.auth.mUser.role === 'admin') {
        this.breadcrumbs = this.breadcrumbs.concat([
          {
            text: 'Dashboard',
            href: '/admin',
            active: false,
          },
          {
            text: 'Content',
            active: true,
          },
        ]);
      } else if (this.$store.state.auth.mUser.role === 'editor') {
        this.breadcrumbs = this.breadcrumbs.concat([
          {
            text: 'Dashboard',
            href: '/admin',
            active: false,
          },
        ]);
      }

      this.breadcrumbs = this.breadcrumbs.concat([
        {
          text: 'Cities',
          href: '/cities',
          active: false,
        },
        {
          text: this.city.name,
          active: true,
        },
      ]);
    },
    async getCity() {
      try {
        this.isLoading = true;

        const response = await axios.get(`${process.env.VUE_APP_API_ROOT}/cities/${this.cityId}`);

        if (response.data.success) {
          this.city = response.data.city;
          await this.checkForPendingCityRequests();
          this.setBreadcrumbs();
          this.cityVersions = this.generateVersionHistory(this.city.versions, this.city);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        this.isLoading = false;
      }
    },
    async checkForPendingCityRequests() {
      // Check to see if there is a pending request for this city
      await this.$store.dispatch('changeRequests/getPendingCities');

      if (this.$store.state.changeRequests.pendingCities.length) {
        this.pendingCityRequest = this.$store.state.changeRequests.pendingCities.find((x) => x.requestable_id === this.city._id);
      } else {
        this.pendingCityRequest = null;
      }

      if (this.pendingCityRequest) {
        this.pendingCityChanges = this.generatePendingChanges(this.pendingCityRequest.listing, this.pendingCityRequest.feedback);
      }

      this.isCityEditAlreadyPending = !!this.pendingCityRequest;
    },
    async approveCityChangeRequest(requestId) {
      try {
        this.isLoading = true;
        const response = await axios.post(`${process.env.VUE_APP_API_ROOT}/cities/approveChangeRequest?id=${requestId}`);
        if (response.data.success) {
          this.$bvToast.toast('Change Request Approved', {
            title: 'Success',
            autoHideDelay: 5000,
            appendToast: true,
          });
        } else {
          // eslint-disable-next-line no-console
          console.log(response.data.message);
          this.$bvToast.toast(`${response.data.message}`, {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        await this.getCity();
        this.isLoading = false;
      }
    },
    async rejectCityChangeRequest(requestId) {
      try {
        this.isLoading = true;
        const response = await axios.post(`${process.env.VUE_APP_API_ROOT}/cities/rejectChangeRequest?id=${requestId}`);
        if (response.data.success) {
          this.$bvToast.toast('Change Request Rejected', {
            title: 'Success',
            autoHideDelay: 5000,
            appendToast: true,
          });
        } else {
          this.$bvToast.toast(`${response.data.message}`, {
            title: 'Error',
            autoHideDelay: 5000,
            appendToast: true,
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        await this.checkForPendingCityRequests();
        this.isLoading = false;
      }
    },
    getCityTypeDisplay(type) {
      switch (type) {
        case 0:
          return 'Major';
        default:
          return 'Minor';
      }
    },
    generateVersionHistory(versions, current) {
      if (!versions || !versions.length) {
        return null;
      }

      const history = [];
      const ignoredKeys = [
        'version',
        'versions',
        'areas',
        'updated_at',
        'created_at',
        'updated_by',
        'insiders_image',
        'insiders_image_updated_at',
        'thumbnail_image_updated_at',
        'thumbnail_image',
        'photos',
      ];

      for (let index = versions.length; index > 0; index--) {
        let versionDiffs = {};
        const currentVersion = versions[index] || current;
        const previousVersion = versions[index - 1];
        versionDiffs = {
          version: currentVersion.version,
          changes: [],
          imageChanges: [],
          updated_at: currentVersion.updated_at,
          oldUpdatedBy: previousVersion.updated_by,
          newUpdatedBy: currentVersion.updated_by,
        };

        Object.keys(currentVersion).forEach((key) => {
          if (!ignoredKeys.includes(key)) {
            if (!deepEqual(currentVersion[key], previousVersion[key])) {
              versionDiffs.changes.push({ key, old: previousVersion[key], new: currentVersion[key] });
            }
          }
        });

        if (currentVersion.insiders_image !== previousVersion.insiders_image) {
          versionDiffs.imageChanges.push({
            key: 'Insider Image',
            old: { image: previousVersion.insiders_image },
            new: { image: currentVersion.insiders_image },
          });
        }

        const prevLength = previousVersion.photos ? previousVersion.photos.length : 0;
        const currLength = currentVersion.photos ? currentVersion.photos.length : 0;
        const maxNumPhotos = Math.max(prevLength, currLength);

        for (let i = 0; i < maxNumPhotos; i++) {
          if (!deepEqual(currentVersion.photos[i], previousVersion.photos[i])) {
            versionDiffs.imageChanges.push({
              key: `Image ${i + 1}`,
              old: previousVersion.photos[i],
              new: currentVersion.photos[i],
            });
          }
        }

        history.push(versionDiffs);
      }

      return history;
    },
    generatePendingChanges(listing, feedback) {
      const changes = {
        fieldChanges: [],
        imageChanges: [],
      };
      const ignoredKeys = [
        'id',
        '_id',
        'version',
        'versions',
        'areas',
        'updated_at',
        'created_at',
        'updated_by',
        'insiders_image',
        'insiders_image_updated_at',
        'thumbnail_image_updated_at',
        'thumbnail_image',
        'photos',
      ];

      delete listing.directions_train;

      Object.keys(listing).forEach((key) => {
        if (!ignoredKeys.includes(key)) {
          changes.fieldChanges.push({ field: key, val: listing[key] });
        }
      });

      if (feedback) {
        changes.fieldChanges.push({ field: 'additional feedback', val: feedback });
      }

      const newImages = listing.photos.filter((x) => !this.city.photos.map((y) => y.image).includes(x.image));
      const removedImages = this.city.photos.filter((x) => !listing.photos.map((y) => y.image).includes(x.image));

      changes.imageChanges.push({ field: 'Images (Added)', val: newImages });
      changes.imageChanges.push({ field: 'Images (Removed)', val: removedImages });

      if (listing.insiders_image !== this.city.insiders_image) {
        changes.imageChanges.push({ field: 'Insider Image (Added)', val: [{ image: listing.insiders_image }] });
        changes.imageChanges.push({ field: 'Insider Image (Removed)', val: [{ image: this.city.insiders_image }] });
      }

      return changes;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-header {
  margin-top: 2rem;
  background-color: white;
  padding: 2rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  h1 {
    margin-top: 0;
  }

  p {
    font-weight: bold;
    color: black;
    margin-bottom: 0;
  }
}

.detail-section {
  margin-top: 2rem;
  background-color: white;
  padding: 2rem;

  h2 {
    margin-top: 2rem;
  }

  p {
    margin-bottom: 0;
  }

  img {
    height: 9rem;
  }
}

.city-admin-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;

  span {
    display: flex;
    img {
      position: relative;
      left: -4px;
      top: -8px;
    }
  }

  div {
    display: flex;
    gap: 1.5rem;
  }
  .btn {
    height: 2.25rem;
    width: auto;
  }
}

.history-table {
  thead {
    width: 100%;
  }

  tbody {
    th {
      span {
        font-weight: 400;
      }
    }

    td {
      overflow-wrap: anywhere;
      max-width: 25rem;
    }
  }
}

.pending-img {
  width: 13rem;
  margin: 5px;
}

.btn {
  width: 10.5rem;
  height: 2.5rem;
}
</style>
